import { Autocomplete, Box, Checkbox, Chip, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Delete, Edit, InfoOutlined, Visibility } from '@mui/icons-material'
import { getThemeColor } from 'src/utils'
import { formatMainComponentIdentification } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { IMainComponent } from 'src/generated-types/main-component'
import { GridActionButton } from '../common/GridActionButton'
import {
  CategoryLabel,
  GroupCardLabel,
  InfoBannerBox,
  ModeBannerBox,
  SelectionArea,
  StyledGroupCard,
  WhiteAutocompleteBox,
} from './CategorySection.styles'
import { IEquipmentTypeGroup, ISavedGroup } from './CategorySection'

interface ICategoryControlsProps {
  group: IEquipmentTypeGroup
  savedGroups: ISavedGroup[]
  currentSelection: IMainComponent[]
  savedEquipmentIds: Set<number>
  editingGroupId: string | null
  viewingGroupId: string | null
  translatedTypeName: string
  onSelectionChange: (newValue: IMainComponent[]) => void
  onViewGroup: (savedGroup: ISavedGroup) => void
  onEditGroup: (savedGroup: ISavedGroup) => void
  onRemoveGroup: (groupId: string) => void
}

export const CategoryControls: React.FC<ICategoryControlsProps> = ({
  group,
  savedGroups,
  currentSelection,
  savedEquipmentIds,
  editingGroupId,
  viewingGroupId,
  translatedTypeName,
  onSelectionChange,
  onViewGroup,
  onEditGroup,
  onRemoveGroup,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['checklist', 'common', 'activity-log-type-description'])

  const hasAlreadyRecordedSelection =
    !editingGroupId && !viewingGroupId && currentSelection.some(e => savedEquipmentIds.has(e.id as number))

  const getEquipmentLabel = (equipment: IMainComponent) =>
    formatMainComponentIdentification(equipment, {
      itemType: equipment.type?.name ? getTranslatedText(equipment.type.name) : undefined,
    })

  const getGroupTooltip = (savedGroup: ISavedGroup) => {
    const eqs = group.equipments.filter(e => savedGroup.equipmentIds.includes(e.id as number))
    return eqs.map(e => getEquipmentLabel(e)).join(', ')
  }

  return (
    <SelectionArea theme={theme}>
      <Flex.Column gap={1.5}>
        <CategoryLabel>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              color: getThemeColor(theme, MUIThemeColors.primaryMain),
            }}
          >
            {t('type', { ns: 'common' })}: {translatedTypeName}
          </Typography>
        </CategoryLabel>

        {hasAlreadyRecordedSelection && (
          <InfoBannerBox theme={theme}>
            <InfoOutlined
              sx={{ fontSize: 16, color: getThemeColor(theme, MUIThemeColors.primaryMain), flexShrink: 0 }}
            />
            <Typography variant="caption" color={getThemeColor(theme, MUIThemeColors.primaryDark)}>
              {t('selection-replaces-recorded', { ns: 'checklist' })}
            </Typography>
          </InfoBannerBox>
        )}

        <Box>
          {editingGroupId &&
            (() => {
              const editingGroup = savedGroups.find(g => g.id === editingGroupId)
              return editingGroup ? (
                <ModeBannerBox theme={theme} mode="editing">
                  <Typography variant="caption" fontWeight={600}>
                    {t('currently-editing', { ns: 'checklist' })}: {getGroupTooltip(editingGroup)}
                  </Typography>
                </ModeBannerBox>
              ) : null
            })()}

          {viewingGroupId &&
            (() => {
              const viewingGroup = savedGroups.find(g => g.id === viewingGroupId)
              return viewingGroup ? (
                <ModeBannerBox theme={theme} mode="viewing">
                  <Typography variant="caption" fontWeight={600}>
                    {t('currently-viewing', { ns: 'checklist' })}: {getGroupTooltip(viewingGroup)}
                  </Typography>
                </ModeBannerBox>
              ) : null
            })()}

          {!editingGroupId && !viewingGroupId && (
            <Autocomplete
              multiple
              size="small"
              options={group.equipments}
              value={currentSelection}
              getOptionLabel={option => getEquipmentLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => onSelectionChange(newValue)}
              disableCloseOnSelect
              PaperComponent={({ children, ...props }) => (
                <WhiteAutocompleteBox {...props} theme={theme}>
                  {children}
                </WhiteAutocompleteBox>
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ backgroundColor: getThemeColor(theme, MUIThemeColors.white) }}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {getEquipmentLabel(option)}
                    {savedEquipmentIds.has(option.id as number) && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({t('Recorded', { ns: 'activity-log-type-description' })})
                      </Typography>
                    )}
                  </Typography>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index })
                  return (
                    <Chip
                      key={option.id as number}
                      size="small"
                      color={!savedEquipmentIds.has(option.id as number) ? 'primary' : 'warning'}
                      variant="outlined"
                      label={getEquipmentLabel(option)}
                      {...tagProps}
                    />
                  )
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={currentSelection.length === 0 ? t('select-equipment', { ns: 'checklist' }) : ''}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}
        </Box>

        {savedGroups.length > 0 && (
          <Flex.Row flexWrap="wrap" gap={1.5}>
            {savedGroups.map(savedGroup => {
              const fullText = getGroupTooltip(savedGroup)
              const isEditing = editingGroupId === savedGroup.id
              const isViewing = viewingGroupId === savedGroup.id
              const isActive = isEditing || isViewing
              return (
                <StyledGroupCard
                  key={savedGroup.id}
                  theme={theme}
                  sx={{
                    borderColor: isActive
                      ? getThemeColor(theme, MUIThemeColors.primaryMain)
                      : getThemeColor(theme, MUIThemeColors.secondaryLight),
                    borderWidth: '1.5px',
                    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Tooltip title={fullText} arrow placement="top">
                        <GroupCardLabel variant="caption" theme={theme} isActive={isActive}>
                          {fullText}
                        </GroupCardLabel>
                      </Tooltip>
                    </Box>
                    <GridActionButton
                      height="25px"
                      width="25px"
                      toolTipTitle={t('view', { ns: 'common' })}
                      onClick={e => {
                        e.stopPropagation()
                        onViewGroup(savedGroup)
                      }}
                      bgColor={
                        isViewing
                          ? getThemeColor(theme, MUIThemeColors.infoLight)
                          : getThemeColor(theme, MUIThemeColors.white)
                      }
                      buttonIcon={
                        <Visibility sx={{ fontSize: 14, color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
                      }
                    />
                    <GridActionButton
                      height="25px"
                      width="25px"
                      toolTipTitle={t('edit', { ns: 'common' })}
                      onClick={e => {
                        e.stopPropagation()
                        onEditGroup(savedGroup)
                      }}
                      buttonIcon={
                        <Edit sx={{ fontSize: 14, color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
                      }
                    />
                    <GridActionButton
                      height="25px"
                      width="25px"
                      toolTipTitle={t('delete', { ns: 'common' })}
                      onClick={e => {
                        e.stopPropagation()
                        onRemoveGroup(savedGroup.id)
                      }}
                      buttonIcon={
                        <Delete sx={{ fontSize: 14, color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
                      }
                    />
                  </Box>
                </StyledGroupCard>
              )
            })}
          </Flex.Row>
        )}
      </Flex.Column>
    </SelectionArea>
  )
}
